import {ChangeEvent, useCallback, useEffect, useMemo} from 'react';
import './style.scss';
import {useParams} from 'react-router-dom';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../../../../i18n/translations';
import {Box, Grid} from '@mui/material';
import {Controller, useForm} from 'react-hook-form';
import {
  CLEAN_ALPHA_STRINGS,
  CLEAN_STRINGS,
  DEFAULT_FORM_VALIDATIONS,
  shiftsBackgroundColors,
} from '../../../../../../../utils/constants';

import {Modal} from '../../../../../../../components/modal/modal';
import FlatInput from '../../../../../../../components/flat-input/flat-input';
import {FlatStartsAtTime} from '../../../../../../../components/flat-starts-at-time/flat-starts-at-time';
import {FlatMultiTextSelect} from '../../../../../../../components/flat-multi-text-select/flat-multi-text-select';
import {
  FlatSelectDropdown,
  Option,
} from '../../../../../../../components/flat-select-dropdown/flat-select-dropdown';

import {FlatColorSelector} from '../../../../../../../components/flat-color-selector/flat-color-selector';
import {Pallete} from '../../../../../../../theme/bluebird';
import {EntityShiftsRepository} from '../../../../../../../repositories/entity-shifts';
import {EntityShift} from '../../../../../../../backend/interfaces/entity-shift';
import {EntitySocialResponse} from '../../../../../../../backend/interfaces/entity-social-response';
import {PutEntityShift} from '../../../../../../../backend/requests/put-entity-shift';
import {PostEntityShift} from '../../../../../../../backend/requests/post-entity-shift';
import {AffiliatedUser} from '../../../../../../../backend/responses/affiliated-user';
import {useSnackBar} from '../../../../../../../contexts/snack-bar/context';
import {getLocalizedFieldValue} from '../../../../../../../utils/functions/get-localized-field-value';

// import { DevTool } from '@hookform/devtools';

export const RESPONSIBLE_NOT_DEFINED = 'not-defined';

interface ShiftForm {
  name: string;
  abbreviation: string;
  socialResponses: Option[];
  maxShiftMembers: number;
  startHour: moment.Moment;
  endHour: moment.Moment;
  responsibleUserId?: string;
  shiftMembers: Option[];
  backgroundColor: string;
  foregroundColor: string;
}

export interface ShiftFormProps {
  originalShift?: EntityShift;
  title: string;
  shiftModalOpened: boolean;
  closeShiftModal: () => void;
  collaborators?: AffiliatedUser[] | null;
  entitySocialResponses?: EntitySocialResponse[] | null;
}

const SHIFT_NAME_MAX_CHARACTERS = 10;

const SHIFT_ABBREVIATION_MAX_CHARACTERS = 3;

const ON_SET_VALUE_EFFECTS = {shouldDirty: true, shouldValidate: true};

const ActionsShiftForm = ({
  title,
  originalShift,
  shiftModalOpened,
  closeShiftModal,
  collaborators,
  entitySocialResponses,
}: ShiftFormProps) => {
  let {entityId}: {entityId?: string} = useParams();

  const snackBarContext = useSnackBar();

  const {t} = useTranslation();

  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    control,
    formState: {errors, isValid},
    reset,
  } = useForm<ShiftForm>({
    ...DEFAULT_FORM_VALIDATIONS,
  });

  const onClose = () => {
    closeShiftModal();
    reset();
  };

  const handleCreateShift = async (data: PostEntityShift) => {
    await EntityShiftsRepository.instance
      .createShift(entityId!, data)
      .then((shift) => {
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(
            translations.shifts_page.shift_modal.shift_form.snackbar.create_shift.success,
            {
              name: shift.name,
            }
          ),
        });
      })
      .catch((e) => {
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.shifts_page.shift_modal.shift_form.snackbar.create_shift.error),
        });

        console.error('🔴 Could not create shift', e);
      })
      .finally(() => {
        onClose();
      });
  };

  const handleUpdateShift = async (shiftId: string, data: PutEntityShift) => {
    await EntityShiftsRepository.instance
      .updateShift(entityId!, shiftId, data)
      .then((shift) => {
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(
            translations.shifts_page.shift_modal.shift_form.snackbar.update_shift.success,
            {
              name: shift.name,
            }
          ),
        });
      })
      .catch((e) => {
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.shifts_page.shift_modal.shift_form.snackbar.update_shift.error),
        });

        console.error('🔴 Could not update shift', e);
      })
      .finally(() => {
        onClose();
      });
  };

  const nameInput = (
    <FlatInput
      label={t(translations.shifts_page.shift_modal.shift_form.name)}
      placeholder={t(translations.shifts_page.shift_modal.shift_form.name_placeholder)}
      {...register('name', {
        required: {
          value: true,
          message: t(translations.shifts_page.shift_modal.shift_form.required_field_message),
        },
        pattern: {
          value: CLEAN_STRINGS,
          message: t(translations.shifts_page.shift_modal.shift_form.pattern_field_message),
        },
        maxLength: {
          value: SHIFT_NAME_MAX_CHARACTERS,
          message: t(translations.shifts_page.shift_modal.shift_form.max_characters_name_message, {
            characters: SHIFT_NAME_MAX_CHARACTERS,
          }),
        },
      })}
      onChange={(e) => {
        setValue('name', e.target.value);
        if (
          !getValues('abbreviation') ||
          !getValues('name') ||
          getValues('abbreviation')?.length < 3
        )
          setValue('abbreviation', e.target.value?.slice(0, 3).toUpperCase(), ON_SET_VALUE_EFFECTS);
      }}
      errors={errors.name?.message}
      counter={
        errors.name?.message
          ? {
              current: getValues('name').length,
              max: SHIFT_NAME_MAX_CHARACTERS,
            }
          : undefined
      }
      required
    />
  );

  const abbreviationInput = (
    <Controller
      name="abbreviation"
      control={control}
      rules={{
        required: {
          value: true,
          message: t(translations.shifts_page.shift_modal.shift_form.min_required_field_message),
        },
        pattern: {
          value: CLEAN_ALPHA_STRINGS,
          message: t(translations.shifts_page.shift_modal.shift_form.pattern_field_message),
        },
        maxLength: {
          value: SHIFT_ABBREVIATION_MAX_CHARACTERS,
          message: t(translations.shifts_page.shift_modal.shift_form.max_characters_name_message, {
            characters: SHIFT_ABBREVIATION_MAX_CHARACTERS,
          }),
        },
      }}
      render={({field: {value, onBlur}}) => {
        const handleOnChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          const uppercaseValue = e.target.value.toUpperCase();
          if (uppercaseValue.length <= 3) {
            setValue('abbreviation', uppercaseValue);
          }
        };

        return (
          <FlatInput
            label={t(translations.shifts_page.shift_modal.shift_form.abbreviation)}
            placeholder={t(
              translations.shifts_page.shift_modal.shift_form.abbreviation_placeholder
            )}
            errors={errors.abbreviation?.message}
            value={value}
            onBlur={onBlur}
            onChange={handleOnChange}
            required
          />
        );
      }}
    />
  );

  const ColorSelectorInput = () => {
    const flattenedShiftsBackgroundColors = Object.entries(shiftsBackgroundColors).reduce(
      (result, [color, shades]) => {
        const entries = Object.entries(shades).map(([shade, value]) => ({
          key: value,
          label: `${color}/${shade}`,
        }));
        // @ts-ignore
        return result.concat(entries);
      },
      []
    );

    const findSelectedOption = (hexColor: string): Option | null => {
      let foundOption = null;

      Object.entries(shiftsBackgroundColors).forEach(([color, shades]) => {
        Object.entries(shades).forEach(([shade, value]) => {
          if (value === hexColor) {
            foundOption = {
              key: hexColor,
              label: `${color}/${shade}`,
            };
          }
        });
      });

      return foundOption;
    };

    const handleOnChange = (option: Option) => {
      if (option.label.includes('/light')) {
        setValue('foregroundColor', Pallete.gray.base, ON_SET_VALUE_EFFECTS);
      } else {
        setValue('foregroundColor', Pallete.white.base, ON_SET_VALUE_EFFECTS);
      }
      setValue('backgroundColor', option.key, ON_SET_VALUE_EFFECTS);
    };

    return (
      <Controller
        name="backgroundColor"
        control={control}
        render={({field: {value, onBlur}}) => (
          <FlatColorSelector
            label={t(translations.shifts_page.shift_modal.shift_form.color)}
            value={findSelectedOption(value)!}
            options={flattenedShiftsBackgroundColors}
            onChange={handleOnChange}
            onBlur={onBlur}
            required
          />
        )}
      />
    );
  };

  const socialResponsesInput = (
    <Controller
      name="socialResponses"
      control={control}
      render={({field: {onChange, value}, fieldState: {error}}) => (
        <Box sx={{'& .MuiFormControl-root': {width: '100%'}}}>
          <FlatMultiTextSelect
            label={t(translations.shifts_page.shift_modal.shift_form.social_response)}
            placeholder={t(
              translations.shifts_page.shift_modal.shift_form.social_responses_placeholder_input
            )}
            options={entitySocialResponses!.map((socialResponse) => ({
              key: socialResponse.id,
              label: getLocalizedFieldValue(socialResponse, 'abbreviation'),
            }))}
            {...{onChange, value}}
            errors={error?.message}
          />
        </Box>
      )}
    />
  );

  const maximumShiftMembersInput = (
    <FlatInput
      type="number"
      label={t(translations.shifts_page.shift_modal.shift_form.people_per_shift)}
      placeholder={t(
        translations.shifts_page.shift_modal.shift_form.people_per_shift_placeholder_input
      )}
      defaultValue={1}
      {...register('maxShiftMembers', {
        min: {
          value: 0,
          message: t(translations.shifts_page.shift_modal.shift_form.pattern_field_message),
        },
        max: {
          value: 99,
          message: t(translations.shifts_page.shift_modal.shift_form.pattern_field_message),
        },
      })}
      inputProps={{
        min: 0,
        max: 99,
      }}
      errors={errors.maxShiftMembers?.message}
    />
  );

  const startHourInput = (
    <Controller
      name="startHour"
      control={control}
      render={({field, fieldState: {error}}) => (
        // @ts-ignore
        <FlatStartsAtTime
          {...field}
          label={t(translations.shifts_page.shift_modal.shift_form.start_hour)}
          errors={error?.message}
        />
      )}
    />
  );

  const endHourInput = (
    <Controller
      name="endHour"
      control={control}
      render={({field, fieldState: {error}}) => (
        // @ts-ignore
        <FlatStartsAtTime
          {...field}
          label={t(translations.shifts_page.shift_modal.shift_form.end_hour)}
          errors={error?.message}
        />
      )}
    />
  );

  const nonSelectedOption: Option = {
    key: RESPONSIBLE_NOT_DEFINED,
    label: t(translations.shifts_page.shift_modal.shift_form.shift_responsible_placeholder),
  };

  const ResponsibleUserInput = () => {
    const options = useMemo(() => {
      let collab = collaborators?.map((collaborator) => {
        return {
          label: collaborator.user.displayName || collaborator.user.name,
          key: collaborator.user.id ?? '',
          picture: collaborator.user.picture || collaborator.user.name,
        } as Option;
      });

      collab?.unshift(nonSelectedOption);

      return collab as Option[];
    }, []);

    return (
      <Controller
        name="responsibleUserId"
        defaultValue={nonSelectedOption.key}
        control={control}
        render={({field: {value}, fieldState: {error}}) => {
          const selected = collaborators?.find((collaborator) => collaborator.user.id === value);

          return (
            <Box sx={{'& .MuiFormControl-root': {width: '100%'}}}>
              <FlatSelectDropdown
                role="user"
                label={t(translations.shifts_page.shift_modal.shift_form.shift_responsible)}
                placeholder={t(
                  translations.shifts_page.shift_modal.shift_form.shift_responsible_placeholder
                )}
                value={
                  selected
                    ? ({
                        key: selected.user.id,
                        label: selected.user.displayName || selected.user.name,
                        picture: selected.user.picture || selected.user.name,
                      } as Option)
                    : nonSelectedOption
                }
                options={options}
                onChange={(option) => setValue('responsibleUserId', option.key)}
                errors={error?.message}
              />
            </Box>
          );
        }}
      />
    );
  };

  const submitShiftForm = async (data: ShiftForm) => {
    const shift: PutEntityShift | PostEntityShift = {
      name: data.name,
      abbreviation: data.abbreviation,
      startHour: data.startHour.clone().set({seconds: 0, milliseconds: 0}).utc().toDate(),
      endHour: data.endHour.clone().set({seconds: 0, milliseconds: 0}).utc().toDate(),
      maxShiftMembers: Number(data.maxShiftMembers),
      backgroundColor: data.backgroundColor,
      foregroundColor: data.foregroundColor,
      responsibleUserId:
        data.responsibleUserId && data.responsibleUserId !== RESPONSIBLE_NOT_DEFINED
          ? data.responsibleUserId
          : undefined,
      socialResponses:
        data.socialResponses && data.socialResponses.map((socialResponse) => socialResponse.key),
    };

    if (originalShift) {
      handleUpdateShift(originalShift.id!, shift);
    } else {
      handleCreateShift(shift);
    }
  };

  const handleSetDefaultValues = useCallback(() => {
    if (originalShift) {
      setValue('name', originalShift.name, ON_SET_VALUE_EFFECTS);
      setValue('abbreviation', originalShift.abbreviation, ON_SET_VALUE_EFFECTS);
      setValue('maxShiftMembers', originalShift.maxShiftMembers!, ON_SET_VALUE_EFFECTS);
      setValue('startHour', moment(originalShift.startHour), ON_SET_VALUE_EFFECTS);
      setValue('endHour', moment(originalShift.endHour), ON_SET_VALUE_EFFECTS);
      setValue('responsibleUserId', originalShift.responsibleUserId!, ON_SET_VALUE_EFFECTS);
      setValue('backgroundColor', originalShift.backgroundColor, ON_SET_VALUE_EFFECTS);
      setValue('foregroundColor', originalShift.foregroundColor, ON_SET_VALUE_EFFECTS);

      if (entitySocialResponses) {
        setValue(
          'socialResponses',
          entitySocialResponses
            .filter((socialResponse) => originalShift.socialResponses?.includes(socialResponse.id!))
            .map((socialResponse) => ({
              key: socialResponse.id!,
              label: getLocalizedFieldValue(socialResponse, 'abbreviation'),
            })),
          ON_SET_VALUE_EFFECTS
        );
      }
    } else {
      setValue('startHour', moment(), ON_SET_VALUE_EFFECTS);
      setValue('endHour', moment().add(1, 'hour'), ON_SET_VALUE_EFFECTS);
      setValue('backgroundColor', shiftsBackgroundColors.dark_gray.dark, ON_SET_VALUE_EFFECTS);
      setValue('foregroundColor', Pallete.white.base, ON_SET_VALUE_EFFECTS);
    }
  }, [entitySocialResponses, originalShift, setValue]);

  useEffect(() => {
    if (shiftModalOpened) handleSetDefaultValues();
  }, [handleSetDefaultValues, originalShift, shiftModalOpened]);

  return (
    <Modal
      id="add-shift-form"
      title={title}
      open={shiftModalOpened}
      actions={[
        {
          label: t(translations.shifts_page.cancel),
          type: 'destructive',
          onClick: onClose,
        },
        {
          label: originalShift
            ? t(translations.shifts_page.save)
            : t(translations.shifts_page.create),
          type: 'nondestructive',
          disabled: !isValid,
          onClick: () => handleSubmit(submitShiftForm)(),
        },
      ]}
    >
      <form id="create-shift-flow-form">
        {/*  <DevTool control={control} /> */}
        <Grid container columnSpacing={2} rowSpacing={3}>
          <Grid item xs={6}>
            {nameInput}
          </Grid>
          <Grid item xs={3}>
            {abbreviationInput}
          </Grid>
          <Grid item xs={3}>
            <ColorSelectorInput />
          </Grid>

          <Grid item xs={6}>
            {socialResponsesInput}
          </Grid>
          <Grid item xs={6}>
            {maximumShiftMembersInput}
          </Grid>

          <Grid item xs={6}>
            {startHourInput}
          </Grid>
          <Grid item xs={6}>
            {endHourInput}
          </Grid>

          <Grid item xs={12}>
            <ResponsibleUserInput />
          </Grid>
        </Grid>
      </form>
    </Modal>
  );
};

export default ActionsShiftForm;
